import React, { FC, useState, useEffect, useMemo } from 'react';
import { AccountContentContainer } from './style';
import FormInputField from 'components/FormInputField';
import { colors } from 'constants/colors';
import Checkbox from 'components/Checkbox'
import { CalendarIcon } from "assets/icons";
import StyledButton from 'components/Button';
import { useLoadTrips } from 'customHooks/useLoadTrips';
import { useParams } from 'react-router-dom';
import ReactDOM from 'react-dom'
import moment from 'moment'
import { useRecoilState } from 'recoil'
import { dateRangeState } from 'states/SearchBarStates/atoms'
import DatePickerModal from 'components/DatePickerModal'
import AdapterMoment from '@material-ui/pickers/adapter/moment'
import { LocalizationProvider } from '@material-ui/pickers'
import { useUpdateBookingDates } from 'customHooks/useUpdateBookingDates'
import { useHistory } from 'react-router-dom'
import { useIsRoomNumberAvailableForDates } from 'customHooks/useIsRoomNumberAvailableForDates';
import { calcModifyDatesPreview } from "utils/calcModifyDatesPreview";

const debug = false;


const ModifyDatesContent: FC = () => {

  const history = useHistory()

  const {
    updateDateForSingleBooking,
    updateDateForGroupBooking,
    loading: updatingDates,
  } = useUpdateBookingDates()

  const { checkRoomNumberAvailability, loading: checkingAvailability } =
    useIsRoomNumberAvailableForDates()

  const [isRoomAvailable, setIsRoomAvailable] = useState(true)
  const [availabilityMessage, setAvailabilityMessage] = useState('')

  const { trips } = useLoadTrips()

  const [openModal, setOpenModal] = useState(false)
  const [dateRange, setDateRange] = useRecoilState(dateRangeState)

  const { groupId } = useParams<{ groupId: string }>()
  const { bookingId } = useParams<{ bookingId: string }>()
  const trip = trips[groupId]

  const booking = trip?.bookings?.find(b => b.bookingId === bookingId) || null;

  const activeBookings = useMemo(() => {
    return trip?.bookings?.filter(
      (b) => !b?.isDeleted && b?.status !== "CANCELLED"
    ) ?? [];
  }, [trip?.bookings]);

  const totalBookings = activeBookings.length

  const groupBookingIds = activeBookings.map((b) => b.bookingId)

  const bookingCheckInISO = booking?.checkInDate
    ? (booking.checkInDate instanceof Date ? booking.checkInDate.toISOString() : String(booking.checkInDate))
    : ''

  const bookingCheckOutISO = booking?.checkOutDate
    ? (booking.checkOutDate instanceof Date ? booking.checkOutDate.toISOString() : String(booking.checkOutDate))
    : ''

  const hasUserChangedDates =
    !!dateRange?.[0] &&
    !!dateRange?.[1] &&
    (dateRange[0] !== bookingCheckInISO || dateRange[1] !== bookingCheckOutISO)

  if (debug) {
    console.log("(Modify Dates) booking", booking);
  }

  const bookedRoomNumber = booking?.roomBookings[0]?.room?.roomNumber;
  const bookedRoomTypeId = booking?.roomBookings[0]?.room?.roomType?.roomTypeId;

  const groupRoomsToCheck = useMemo(() => {
    return activeBookings.map((b) => ({
      bookingId: b.bookingId,
      roomNumber: b?.roomBookings?.[0]?.room?.roomNumber,
      roomTypeId: b?.roomBookings?.[0]?.room?.roomType?.roomTypeId,
    }));
  }, [activeBookings])

  if (debug) {
    console.log("(Modify Dates) activeBookings", activeBookings);
    console.log("(Modify Dates) groupBookingIds", groupBookingIds);
    console.log("(Modify Dates) groupRoomsToCheck", groupRoomsToCheck);
  }

  if (debug) {
    console.log("(Modify Dates) trip", trip);
    console.log("(Modify Dates) bookedRoomNumber", bookedRoomNumber);
    console.log("(Modify Dates) bookedRoomTypeId", bookedRoomTypeId);
    console.log("(Modify Dates) groupRoomsToCheck", groupRoomsToCheck);
  }

  const isDatesInvalid =
    !dateRange?.[0] ||
    !dateRange?.[1] ||
    moment(dateRange[0]).toString() === 'Invalid date' ||
    moment(dateRange[1]).toString() === 'Invalid date' ||
    moment(dateRange[0]).isSameOrAfter(moment(dateRange[1]))

  const updateInput = {
    checkInDate: String(dateRange?.[0] ?? ''),
    checkOutDate: String(dateRange?.[1] ?? ''),
  }


  if (debug) {
    console.log("(Modify Dates) groupId (from url): ", groupId);
    console.log("(Modify Dates) bookingId (from url): ", bookingId);
    console.log("(Modify Dates) Currnet booking: ", booking);
  }




  const [applyThisChangeToAllRooms, setApplyThisChangeToAllRooms] = useState(false);

  const checkInLabelValue = dateRange?.[0]
    ? moment(dateRange[0]).format('ddd, DD MMM')
    : ''

  const checkOutLabelValue = dateRange?.[1]
    ? moment(dateRange[1]).format('ddd, DD MMM')
    : ''

  useEffect(() => {
    if (!booking?.checkInDate || !booking?.checkOutDate) return

    const checkIn =
      booking.checkInDate instanceof Date
        ? booking.checkInDate.toISOString()
        : String(booking.checkInDate)

    const checkOut =
      booking.checkOutDate instanceof Date
        ? booking.checkOutDate.toISOString()
        : String(booking.checkOutDate)

    setDateRange([checkIn, checkOut])
  }, [booking?.checkInDate, booking?.checkOutDate, setDateRange])


  useEffect(() => {

    if (openModal) return

    if (!hasUserChangedDates) {
      setIsRoomAvailable((prev) => (prev === true ? prev : true))
      setAvailabilityMessage((prev) => (prev === '' ? prev : ''))
      return
    }

    const runCheck = async () => {
      if (isDatesInvalid) {
        setIsRoomAvailable(false)
        setAvailabilityMessage('Please select valid dates.')
        return
      }

      try {
        // If checkbox is ON and we have a group — check every room in the group
        if (applyThisChangeToAllRooms && groupRoomsToCheck.length > 1) {
          // if any booking has missing data — block (safe mode)
          const hasMissing = groupRoomsToCheck.some(
            (x) => !x.bookingId || !x.roomNumber || !x.roomTypeId
          )
          if (hasMissing) {
            setIsRoomAvailable(false)
            setAvailabilityMessage('We cannot confirm availability for all rooms. Please try again.')
            return
          }

          const results = await Promise.all(
            groupRoomsToCheck.map((x) =>
              checkRoomNumberAvailability({
                bookingId: x.bookingId,
                roomNumber: String(x.roomNumber),
                roomTypeId: String(x.roomTypeId),
                checkInDate: updateInput.checkInDate,
                checkOutDate: updateInput.checkOutDate,
              })
            )
          )

          const allAvailable = results.every((r) => r.isAvailable)

          if (allAvailable) {
            setIsRoomAvailable(true)
            setAvailabilityMessage('')
          } else {
            setIsRoomAvailable(false)
            setAvailabilityMessage(
              'One or more rooms are not available for the selected dates. Please choose different dates.'
            )
          }

          return
        }

        // Otherwise — check only current booking room
        if (!bookingId) return
        if (!bookedRoomNumber || !bookedRoomTypeId) return

        const checkRoomNumberAvailabilityPayload = {
          bookingId,
          roomNumber: String(bookedRoomNumber),
          roomTypeId: String(bookedRoomTypeId),
          checkInDate: updateInput.checkInDate,
          checkOutDate: updateInput.checkOutDate,
        };

        if (debug) {
          console.log("(Modify Dates) checkRoomNumberAvailability PAYLOAD", checkRoomNumberAvailabilityPayload);
        }

        const res = await checkRoomNumberAvailability(checkRoomNumberAvailabilityPayload);

        if (debug) {
          console.log("(Modify Dates) checkRoomNumberAvailability RESPONSE", res);
        }

        if (res.isAvailable) {
          setIsRoomAvailable(true)
          setAvailabilityMessage('')
        } else {
          setIsRoomAvailable(false)
          setAvailabilityMessage(
            'This room is not available for the selected dates. Please choose different dates.'
          )
        }
      } catch (e) {
        setIsRoomAvailable(false)
        setAvailabilityMessage(
          'We cannot confirm availability for the selected dates. Please try again.'
        )
      }
    }

    runCheck()

  }, [
    bookingId,
    bookedRoomNumber,
    bookedRoomTypeId,
    updateInput.checkInDate,
    updateInput.checkOutDate,
    isDatesInvalid,
    checkRoomNumberAvailability,
    hasUserChangedDates,
    applyThisChangeToAllRooms,
    groupRoomsToCheck,
    openModal
  ])


  const handleDatesChange = () => {
    setOpenModal(true)
  }

  const handleDatesUpdate = async () => {
    if (!bookingId) return
    if (isDatesInvalid) return

    try {
      if (applyThisChangeToAllRooms && groupBookingIds.length > 1) {
        await updateDateForGroupBooking(groupBookingIds, updateInput)
      } else {
        await updateDateForSingleBooking(bookingId, updateInput)
      }

      // After successful update — redirect back to booking page
      setTimeout(() => {
        history.push(`/account/bookings/${trip?.groupId}`)
      }, 600)
    } catch (e) {
      // errors already handled in hook (global alerts)
    }
  }


  const canShowBookingChanges =
    !openModal &&
    hasUserChangedDates &&
    !isDatesInvalid &&
    isRoomAvailable &&
    !checkingAvailability;

  const preview = useMemo(() => {
    if (!canShowBookingChanges) return null;

    return calcModifyDatesPreview({
      trip,
      bookingId: String(bookingId || ""),
      dateRange: dateRange as any,
      applyToAll: !!applyThisChangeToAllRooms,
      hasUserChangedDates,
      isDatesInvalid,
      roomsOnly: true,
    });
  }, [
    canShowBookingChanges,
    trip,
    bookingId,
    dateRange,
    applyThisChangeToAllRooms,
    hasUserChangedDates,
    isDatesInvalid,
  ]);

  const money = (n: number) => `$${Number(n || 0).toFixed(2)}`;

  if (debug) {
    console.log("(Modify Dates) booking.status", booking?.status);
    console.log("(Modify Dates) roomBookings", booking?.roomBookings);
    console.log("(Modify Dates) bookedRoomNumber", bookedRoomNumber);
    console.log("(Modify Dates) bookedRoomTypeId", bookedRoomTypeId);
    console.log("(Modify Dates) applyThisChangeToAllRooms", applyThisChangeToAllRooms);
    console.log("(Modify Dates) totalBookings", totalBookings);

  }

  const datesLabelValue =
    checkInLabelValue && checkOutLabelValue
      ? `${checkInLabelValue} - ${checkOutLabelValue}`
      : '';

  const handleCancel = () => {
    history.push(`/account/bookings/${groupId}`)
  }

  return (
    <AccountContentContainer>
      <div className="cardContent">
        <div className="datesText">
          Select new check-in and check-out dates for your booking. Please note that changing your dates may affect the nightly rate based on availability and demand.
        </div>
        <div className="bookingDates">
          <div className="checkInOut">
            <FormInputField
              className="secInput"
              label="DATE"
              type="text"
              borderColor={colors.signInBorderColor}
              value={datesLabelValue}
              onClick={handleDatesChange}
              labelWeight="400"
              inputHeight="48px"
            />

            <CalendarIcon
              className="calendarIcon"
              onClick={handleDatesChange}
            />
          </div>
        </div>
        {totalBookings > 1 &&
          (
            <Checkbox
              className="accountCheckbox"
              checked={applyThisChangeToAllRooms}
              //error={termsOfServiceError}
              borderColor={colors.signInBorderColor}
              label="Apply this change to all rooms"
              onChange={() => {
                setApplyThisChangeToAllRooms(!applyThisChangeToAllRooms);
              }}
              checkboxScale={1.2}
            />
          )
        }
        {availabilityMessage && (
          <div className="datesAvailabilityError">
            {availabilityMessage}
          </div>
        )}



        {canShowBookingChanges && preview && (
          <div className="bookingChanges">
            <div className="bookingOverview">OVERVIEW</div>

            {preview.lines
              .filter((l) => String(l.key).startsWith("booking_"))
              .map((l) => {
                const isModified = !!(l as any).isModified;

                return (
                  <div className="bookingOverviewLine" key={l.key}>
                    <div className={`bookingOverviewTitle ${isModified ? "priceAdd" : ""}`}>
                      <div className="bookingNameAndPrice">{l.title}</div>
                    </div>
                    <div className="bookingOverviewDots">
                      <div className="dashedLine"></div>
                    </div>
                    <div className={`bookingOverviewNumber ${isModified ? "priceAdd" : ""}`}>
                      {money(Number(l.amount || 0))}
                    </div>
                  </div>
                );
              })}

            <div className="bookingOverviewLine" key="mat">
              <div className="bookingOverviewTitle">
                <div className="bookingNameAndPrice priceAdd">Municipal Accommodation Tax (6%)</div>
              </div>
              <div className="bookingOverviewDots">
                <div className="dashedLine"></div>
              </div>
              <div className="bookingOverviewNumber priceAdd">
                {money(Number(preview.lines.find((x) => x.key === "mat")?.amount || 0))}
              </div>
            </div>

            <div className="bookingOverviewLine" key="hst">
              <div className={`bookingOverviewTitle`}>
                <div className="bookingNameAndPrice priceAdd">HST (13%)</div>
              </div>
              <div className="bookingOverviewDots">
                <div className="dashedLine"></div>
              </div>
              <div className="bookingOverviewNumber priceAdd">
                {money(Number(preview.lines.find((x) => x.key === "hst")?.amount || 0))}
              </div>
            </div>

            <div className="bookingOverviewTotal">
              <div className="bookingOverviewTotalTitle">YOUR NEW TOTAL</div>
              <div className="bookingOverviewDots"></div>
              <div className="bookingOverviewNumber">{money(Number(preview.total || 0))}</div>
            </div>
          </div>
        )}


        <div className="buttonContainer">
        <div className="cancelBt" onClick={handleCancel}>
          Cancel
        </div>
          <StyledButton
            width="157px"
            height="48px"
            disabled={updatingDates || checkingAvailability || isDatesInvalid || !isRoomAvailable}
            onClick={handleDatesUpdate}
          >
            {updatingDates ? 'Saving...' : checkingAvailability ? 'Checking...' : 'Modify Booking'}
          </StyledButton>
        </div>
      </div>
      {openModal &&
        ReactDOM.createPortal(
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePickerModal openModal={openModal} setOpenModal={setOpenModal} />
          </LocalizationProvider>,
          document.body
        )
      }
    </AccountContentContainer>
  );
};

export default ModifyDatesContent;
